import { Calendar } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import CalendarContextMenu from './CalendarContextMenu';

export default class VtCalendarManager {
  constructor() {
    this.calendar = null;
    this.currentActionExecutionId = null;
    this.currentActionAlreadyReserved = false;
    this.$container = $('#vt-modal-calendar-container');
    this.$modal = $('#vtPlanningModal');

    if (!this.$container.length || !this.$modal.length) {
      return;
    }

    this.calendarUrl = this.$container.data('calendar-url');
    this.bookUrl = this.$container.data('book-url');
    this.taskId = this.$container.data('task-id');

    this.contextMenu = new CalendarContextMenu();
    this.bindPlanButtons();
    this.bindModalEvents();
  }

  initCalendar() {
    const calendarEl = document.getElementById('vt-modal-calendar');
    if (!calendarEl) {
      return;
    }

    this.calendar = new Calendar(calendarEl, {
      plugins: [dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin],
      initialView: 'dayGridMonth',
      locale: 'fr',
      timeZone: 'Europe/Paris',
      headerToolbar: {
        left: 'prev,next today',
        center: 'title',
        right: 'dayGridMonth,timeGridWeek,timeGridDay'
      },
      buttonText: {
        today: "Aujourd'hui",
        month: 'Mois',
        week: 'Semaine',
        day: 'Jour'
      },
      slotMinTime: '07:00:00',
      slotMaxTime: '20:00:00',
      allDaySlot: false,
      selectable: true,
      height: 'auto',

      events: (info, successCallback, failureCallback) => {
        this.fetchEvents(info.startStr, info.endStr, successCallback, failureCallback);
      },

      eventClick: (info) => {
        const eventType = info.event.extendedProps?.type;
        if (eventType === 'reservation') {
          this.showError(I18n.t('backoffice.moe_plannings.slot_conflict'));
          return;
        }

        // Clicking an existing availability slot reserves it by ID.
        const slotId = info.event.extendedProps?.slot_id;
        this.bookSlot(null, null, slotId);
      },

      select: (info) => {
        if (info.allDay) { return; }
        this.bookSlot(info.startStr, info.endStr);
      },

      // Single click books a 1-hour slot, matching user expectation.
      dateClick: (info) => {
        if (info.allDay) { return; }
        const startsAt = info.date;
        const endsAt = new Date(startsAt.getTime() + (60 * 60 * 1000));
        this.bookSlot(startsAt.toISOString(), endsAt.toISOString());
      },

      eventDidMount: this.contextMenu.getEventDidMount((actionExecutionId, _message) => {
        this.calendar.refetchEvents();
        if (actionExecutionId) {
          this.updatePlanButtonsState(actionExecutionId, false);
          this.clearReservationText(actionExecutionId);
        }
      })
    });

    this.calendar.render();
  }

  fetchEvents(start, end, successCallback, failureCallback) {
    $.ajax({
      url: this.calendarUrl,
      method: 'GET',
      data: { task_id: this.taskId, start: start, end: end },
      headers: { 'Accept': 'application/json' },
      success: (data) => { successCallback(data); },
      error: (xhr) => {
        console.error('Failed to fetch VT calendar events:', xhr.responseText);
        failureCallback(xhr);
      }
    });
  }

  bookSlot(startsAt, endsAt, moeSlotId) {
    if (!this.currentActionExecutionId) {
      this.showError(I18n.t('backoffice.moe_plannings.select_action_first'));
      return;
    }

    if (this.currentActionAlreadyReserved) {
      this.showError(I18n.t('backoffice.moe_plannings.action_already_booked'));
      return;
    }

    const token = $('meta[name="csrf-token"]').attr('content');

    const payload = { action_execution_id: this.currentActionExecutionId };
    if (moeSlotId) {
      payload.moe_slot_id = moeSlotId;
    } else {
      payload.starts_at = startsAt;
      payload.ends_at = endsAt;
    }

    $.ajax({
      url: this.bookUrl,
      method: 'POST',
      headers: { 'X-CSRF-Token': token, 'Accept': 'application/json' },
      contentType: 'application/json',
      data: JSON.stringify(payload),
      success: (data) => {
        if (data.success) {
          this.calendar.refetchEvents();
          this.currentActionAlreadyReserved = true;
          this.updatePlanButtonsState(this.currentActionExecutionId, true);
          this.setReservationText(this.currentActionExecutionId, data.reservation.starts_at, data.reservation.ends_at);
          this.showBookingInfo(data.message);
          this.showSuccess(data.message);
        }
      },
      error: (xhr) => {
        const resp = xhr.responseJSON;
        if (xhr.status === 409) {
          this.showError(resp ? resp.error : I18n.t('backoffice.moe_plannings.slot_conflict'));
        } else {
          this.showError(resp ? resp.error : I18n.t('common.error'));
        }
      }
    });
  }

  bindPlanButtons() {
    $(document).off('click.vtPlanning', '.vt-plan-moe-btn').on('click.vtPlanning', '.vt-plan-moe-btn', (event) => {
      const $button = $(event.currentTarget);

      this.currentActionExecutionId = $button.data('action-execution-id');
      this.currentActionAlreadyReserved = $button.attr('data-has-reservation') === 'true';

      $('#vt-selected-action-name').text($button.data('action-name') || '-');

      if (this.currentActionAlreadyReserved) {
        this.showBookingInfo(I18n.t('backoffice.moe_plannings.action_already_booked'));
      } else {
        this.hideBookingInfo();
      }
    });
  }

  bindModalEvents() {
    this.$modal.off('shown.bs.modal.vtPlanning').on('shown.bs.modal.vtPlanning', () => {
      if (!this.calendar) {
        this.initCalendar();
      } else {
        this.calendar.updateSize();
        this.calendar.refetchEvents();
      }
    });

    this.$modal.off('hidden.bs.modal.vtPlanning').on('hidden.bs.modal.vtPlanning', () => {
      this.currentActionExecutionId = null;
      this.currentActionAlreadyReserved = false;
      this.hideBookingInfo();
      $('#vt-selected-action-name').text('-');
    });
  }

  updatePlanButtonsState(actionExecutionId, hasReservation) {
    const $btn = $(`.vt-plan-moe-btn[data-action-execution-id='${actionExecutionId}']`);
    $btn.attr('data-has-reservation', hasReservation ? 'true' : 'false')
        .data('has-reservation', hasReservation);

    if (hasReservation) {
      $btn.removeClass('btn-outline-primary').addClass('btn-outline-success');
      $btn.find('i').removeClass('fa-calendar-plus').addClass('fa-calendar-check');
    } else {
      $btn.removeClass('btn-outline-success').addClass('btn-outline-primary');
      $btn.find('i').removeClass('fa-calendar-check').addClass('fa-calendar-plus');
    }
  }

  setReservationText(actionExecutionId, startsAt, endsAt) {
    const $btn = $(`.vt-plan-moe-btn[data-action-execution-id='${actionExecutionId}']`);
    const tz = 'Europe/Paris';
    const startDate = new Date(startsAt);
    const endDate = new Date(endsAt);
    const dayMonth = new Intl.DateTimeFormat('fr-FR', { timeZone: tz, day: 'numeric', month: 'short' }).format(startDate);
    const startTime = new Intl.DateTimeFormat('fr-FR', { timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false }).format(startDate);
    const endTime = new Intl.DateTimeFormat('fr-FR', { timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false }).format(endDate);
    $btn.next('small.vt-reservation-date').remove();
    $btn.after(`<small class="d-block text-success mt-1 vt-reservation-date">${dayMonth} ${startTime} → ${endTime}</small>`);
  }

  clearReservationText(actionExecutionId) {
    $(`.vt-plan-moe-btn[data-action-execution-id='${actionExecutionId}']`).next('small.vt-reservation-date').remove();
  }

  showBookingInfo(text) {
    $('#vt-booking-info-text').text(text);
    $('#vt-booking-info').show();
  }

  hideBookingInfo() {
    $('#vt-booking-info').hide();
  }

  showSuccess(message) {
    $.toast({
      heading: I18n.t('common.success'),
      text: message,
      icon: 'success',
      position: 'top-right',
      loader: true,
      loaderBg: '#28a745'
    });
  }

  showError(message) {
    $.toast({
      heading: I18n.t('common.error'),
      text: message,
      icon: 'error',
      position: 'top-right',
      loader: true,
      loaderBg: '#dc3545'
    });
  }

  destroy() {
    $(document).off('click.vtPlanning', '.vt-plan-moe-btn');
    if (this.$modal && this.$modal.length) {
      this.$modal.off('shown.bs.modal.vtPlanning');
      this.$modal.off('hidden.bs.modal.vtPlanning');
    }

    if (this.calendar) {
      this.calendar.destroy();
      this.calendar = null;
    }
  }
}
